import omit from "lodash/omit";

/**
 * @class QueryStringHelper
 * @description Class used for accessing query string values.
 * @author Allan A. Chua
 * @version 1.0
 * @since November 28, 2018
 */
export default class QueryStringHelper {
  /**
   * @function get
   * @description Method used for retrieving query strinb values.
   * @param {String} name - Name of query string parameter to retrieve.
   * @returns {String} Value associated with the provided key.
   */
  get(name) {
    if (name === "redirect") {
      return this.getRedirectUrl();
    }

    let url = window.location.href;
    name = name.replace(/[[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  /**
   * @function get
   * @description Method used for retrieving query string param specific for redirect urls
   * @param {String} name - Name of query string parameter to retrieve.
   * @returns {String} Value associated with the provided key.
   */
  getRedirectUrlFriendly(name) {
    let url = window.location.href;
    name = name.replace(/[[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^#]*)|#|$)"),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  /**
   * @todo Deprecate. No apparent use case for the append functionality. Rework
   *     get() to use URLSearchParams and use that instead.
   *
   * @function getRedirectUrl
   * @description Method for generating the 'decoded' redirect param and appends the
   *     other remaining params if they exist.
   *     ex. if current location is 'https://foo.com/?redirect=https%3A%2F%2Fbar.com&param1=a&param2=b
   *         the result would be 'https://bar.com&param1=a&param2=b'.
   * @returns {String} Can be an empty string.
   */
  getRedirectUrl() {
    if (window.location.search === "") return "";

    const searchParams = new URLSearchParams(window.location.search);
    const searchParamsObject = Object.fromEntries(searchParams);

    let redirectUrl = searchParamsObject.redirect;
    if (!redirectUrl) return "";

    const otherParams = omit(searchParamsObject, "redirect");
    const otherParamsUrl = new URLSearchParams(otherParams).toString();
    if (otherParamsUrl) redirectUrl += `&${otherParamsUrl}`;

    return redirectUrl;
  }

  checkUrlQueries = () => {
    let currentQuery = window.location.search;

    if (currentQuery) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * Build url query parameters
   * @param {Object} params Query parameters
   * @returns {String} compiled query parameters
   */
  buildQuery(params) {
    if (!params.constructor === Object) {
      return "";
    }

    const keys = Object.keys(params);
    if (keys.length) {
      const encodeQuery = (key) => {
        return [key, encodeURIComponent(params[key])].join("=");
      };

      let queryString = "?";
      queryString += encodeQuery(keys[0]);
      keys.shift();

      if (!keys.length) {
        return queryString;
      }

      for (const key of keys) {
        queryString += "&";
        queryString += encodeQuery(key);
      }

      return queryString;
    }

    return "";
  }

  /**
   * Build url path with query parameters
   * @param {String} baseUrl Base url
   * @param {Object} params Query parameters
   * @returns {String} url with compiled query parameters
   */
  buildURLWithQuery(baseUrl, params = {}) {
    const query = this.buildQuery(params);
    return [baseUrl, query].join("");
  }

  isSameNavigationUrl(fullUrl = "", navigationUrl = "") {
    fullUrl = fullUrl.endsWith("/") ? fullUrl : fullUrl + "/";
    navigationUrl = navigationUrl.endsWith("/") ? navigationUrl : navigationUrl + "/";
    return fullUrl.includes(navigationUrl);
  }
}
